import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./FormationInformation.module.sass";
import Item from "../Item";
import TextInput from "../../../components/TextInput";
import Dropdown from "../../../components/Dropdown";
import { Link } from "react-router-dom";
import { getFormations } from '../../../services/formation.service';
import { getPromotionsByFormation } from '../../../services/promotion.service';

const FormationInformation = ({ className, data, onSelectFormation, onSelectPromotion, onTrainingDetailsChange }) => {
  // États pour les formations et promotions
  const [formations, setFormations] = useState([]);
  const [loadingFormations, setLoadingFormations] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [loadingPromotions, setLoadingPromotions] = useState(false);
  const [selectedFormation, setSelectedFormation] = useState(null);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  
  // États pour les détails de formation
  const [courseDuration, setCourseDuration] = useState('');
  const [numberOfHoursInStudy, setNumberOfHoursInStudy] = useState('');

  // Charger les formations au montage du composant
  useEffect(() => {
    const fetchFormations = async () => {
      setLoadingFormations(true);
      try {
        const formationData = await getFormations();
        setFormations(formationData || []);
        
        // Si une formation est déjà sélectionnée, récupérer ses promotions
        if (data?.formation) {
          setSelectedFormation(data.formation);
          await loadPromotions(data.formation);
        }
        if (data?.promotion) {
          setSelectedPromotion(data.promotion);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des formations", error);
        setFormations([]);
      } finally {
        setLoadingFormations(false);
      }
    };

    fetchFormations();
  }, [data]);

  // Charger les promotions d'une formation
  const loadPromotions = async (formationId) => {
    if (!formationId) {
      setPromotions([]);
      return;
    }
    
    setLoadingPromotions(true);
    try {
      const promotionData = await getPromotionsByFormation(formationId);
      setPromotions(promotionData || []);
    } catch (error) {
      console.error("Erreur lors de la récupération des promotions", error);
      setPromotions([]);
    } finally {
      setLoadingPromotions(false);
    }
  };

  // Gérer la sélection d'une formation
  const handleFormationChange = async (formationId) => {
    setSelectedFormation(formationId);
    setSelectedPromotion(null); // Reset promotion when formation changes
    
    // Charger les promotions de la formation sélectionnée
    await loadPromotions(formationId);
    
    // Transmettre la formation sélectionnée au composant parent
    if (onSelectFormation) {
      onSelectFormation(formationId);
    }
  };

  // Gérer la sélection d'une promotion
  const handlePromotionChange = (promotionId) => {
    setSelectedPromotion(promotionId);
    
    // Transmettre la promotion sélectionnée au composant parent
    if (onSelectPromotion) {
      onSelectPromotion(promotionId);
    }
  };

  // Fonction pour transmettre les détails de formation au parent
  const updateTrainingDetails = () => {
    if (typeof onTrainingDetailsChange === 'function') {
      onTrainingDetailsChange({
        courseDuration: parseInt(courseDuration) || 0,
        numberOfHoursInStudy: parseInt(numberOfHoursInStudy) || 0
      });
    }
  };

  // Mettre à jour le parent à chaque changement
  useEffect(() => {
    updateTrainingDetails();
  }, [courseDuration, numberOfHoursInStudy]);

  // Créer les options pour les dropdowns
  const formationOptions = formations.map(f => ({ value: f.id, title: f.exact_title_of_qualification }));
  const promotionOptions = promotions.map(p => ({ value: p.id, title: p.title_promotion }));

  return (
    <Item
      className={cn(styles.card, className)}
      title="Formation"
      classTitle="title-blue"
    >
      <div className={styles.fieldset}>
        <div className={styles.group}>
          <div className={styles.field}>
            {loadingFormations ? (
              <p>Chargement des formations...</p>
            ) : (
              <Dropdown
                className={styles.dropdown}
                label="Sélectionner une formation"
                name="formation"
                options={formationOptions}
                value={formationOptions.find(opt => opt.value === selectedFormation) || ''}
                setValue={(option) => handleFormationChange(option.value)}
                required
                placeholder="Choisir une formation"
              />
            )}
          </div>

          <div className={styles.field}>
            {loadingPromotions ? (
              <p>Chargement des promotions...</p>
            ) : (
              <Dropdown
                className={styles.dropdown}
                label="Sélectionner une promotion"
                name="promotion"
                options={promotionOptions}
                value={promotionOptions.find(opt => opt.value === selectedPromotion) || ''}
                setValue={(option) => handlePromotionChange(option.value)}
                disabled={!selectedFormation || promotions.length === 0}
                placeholder={!selectedFormation ? "Sélectionnez d'abord une formation" : "Aucune promotion disponible"}
                required
              />
            )}
          </div>
        </div>
      </div>
    </Item>
  );
};

export default FormationInformation;